import React, { useEffect, useMemo, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { autocompletion, type CompletionContext, type Completion } from '@codemirror/autocomplete';
import { hoverTooltip } from '@codemirror/view';
import { EditorView } from '@codemirror/view';
import { linter, type Diagnostic } from '@codemirror/lint';
import { StreamLanguage } from '@codemirror/language';
import { CircleHelp, Columns3, Copy, FlaskConical, Search, Sparkles, Table2 } from 'lucide-react';
import { fieldClass, ghostBtn, iconBtn } from '../components/dataModelsUi';
import { logDataModels } from '../dataModelsLogger';
import ExpressionCopilotDialog from './ExpressionCopilotDialog';
import ExpressionHelpDialog from './ExpressionHelpDialog';
import { validateExpression } from './parser';
import { resolveDirectoryUserId } from './policyDirectory';
import QueryBuilderDialog from './QueryBuilderDialog';
import { testExpressionQuery, userById, type QueryTestResult } from './queryTest';
import { columnGroups, usePolicyAssetColumns } from './usePolicyAssetColumns';
import { usePolicyDirectory } from './usePolicyDirectory';
import type { PolicyAsset, PolicyType } from './types';
import { ALL_VARIABLES, FUNCTION_REGISTRY } from './variables';

const KEYWORD_OPS = [
  { label: 'AND', type: 'keyword', detail: 'Logical AND' },
  { label: 'OR', type: 'keyword', detail: 'Logical OR' },
  { label: 'NOT', type: 'keyword', detail: 'Logical NOT' },
  { label: 'IN', type: 'keyword', detail: 'Membership' },
  { label: 'NOT IN', type: 'keyword', detail: 'Negated membership' },
  { label: 'IS NULL', type: 'keyword', detail: 'Null check' },
  { label: 'IS NOT NULL', type: 'keyword', detail: 'Not-null check' },
  { label: '=', type: 'operator', detail: 'Equals' },
  { label: '!=', type: 'operator', detail: 'Not equals' },
  { label: '>', type: 'operator', detail: 'Greater than' },
  { label: '<', type: 'operator', detail: 'Less than' },
  { label: '>=', type: 'operator', detail: 'Greater than or equal' },
  { label: '<=', type: 'operator', detail: 'Less than or equal' },
];

const DSL = StreamLanguage.define({
  token(stream) {
    if (stream.eatSpace()) return null;
    if (stream.match('${')) {
      stream.eatWhile(/[^}]/);
      stream.eat('}');
      return 'variableName';
    }
    if (stream.match(/'(?:[^'\\]|\\.)*'/)) return 'string';
    if (stream.match(/"(?:[^"\\]|\\.)*"/)) return 'string';
    if (stream.match(/^-?\d+(\.\d+)?/)) return 'number';
    if (stream.match(/^(AND|OR|NOT|IN|IS|NULL)\b/i)) return 'keyword';
    if (stream.match(/^(LOWER|UPPER|COALESCE|STARTS_WITH|ENDS_WITH|CONTAINS|CURRENT_DATE|CURRENT_TIMESTAMP)\b/i)) return 'atom';
    if (stream.match(/^[A-Za-z_][A-Za-z0-9_.]*/)) return 'propertyName';
    if (stream.match(/^(!=|>=|<=|=|>|<)/)) return 'operator';
    stream.next();
    return null;
  },
});

function completions(assets: PolicyAsset[]) {
  return (context: CompletionContext) => {
    const before = context.state.sliceDoc(0, context.pos);
    const variableOpen = before.lastIndexOf('${');
    const variableClose = before.lastIndexOf('}');
    if (variableOpen >= 0 && variableOpen > variableClose) {
      const typed = before.slice(variableOpen + 2);
      const options: Completion[] = ALL_VARIABLES.filter((item) => item.path.startsWith(typed) || item.path.split('.').pop()?.startsWith(typed))
        .map((item) => ({
          label: item.path,
          type: 'variable',
          detail: `${item.type} — ${item.example}`,
          info: item.description,
          apply: `${item.path}}`,
        }));
      return { from: variableOpen + 2, options, validFor: /[\w.]*/ };
    }

    const word = context.matchBefore(/[A-Za-z_$.][\w.]*/);
    if (!word && !context.explicit) return null;
    const query = (word?.text ?? '').toLowerCase();
    const columns = columnGroups(assets).flatMap((group) => group.columns.map((column) => ({
      label: column.qualified,
      type: 'property' as const,
      detail: column.label,
      info: group.label,
      apply: column.qualified,
    })));
    const functions = FUNCTION_REGISTRY.map((item) => ({
      label: item.name,
      type: 'function',
      detail: item.signature,
      info: item.description,
      apply: item.name.startsWith('CURRENT') ? item.name : `${item.name}()`,
    }));
    const variables = ALL_VARIABLES.map((item) => ({
      label: `\${${item.path}}`,
      type: 'variable',
      detail: `${item.type} — ${item.example}`,
      info: item.description,
    }));
    const options = [...columns, ...functions, ...KEYWORD_OPS, ...variables].filter((item) => item.label.toLowerCase().includes(query));
    return { from: word?.from ?? context.pos, options, validFor: /^[\w.${}]*$/ };
  };
}

function SchemaColumnsButton({
  assets,
  isLight,
  onCopy,
}: {
  assets: PolicyAsset[];
  isLight: boolean;
  onCopy: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const { assets: withColumns, loading, error } = usePolicyAssetColumns(assets, { enabled: open || assets.some((asset) => !asset.columns.length) });
  const groups = columnGroups(withColumns);
  const needle = query.trim().toLowerCase();
  const visible = groups
    .map((group) => ({
      ...group,
      columns: group.columns.filter((column) => (
        !needle
        || column.name.toLowerCase().includes(needle)
        || column.qualified.toLowerCase().includes(needle)
        || column.label.toLowerCase().includes(needle)
      )),
    }))
    .filter((group) => group.columns.length);
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const panel = isLight ? 'border-[#E6E8EB] bg-white shadow-slate-200/80' : 'border-white/10 bg-black';

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        className={`${iconBtn(isLight)} !h-8 !w-8 !rounded-none`}
        aria-expanded={open}
        aria-label="Schema columns"
        title="Schema columns"
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            if (next) logDataModels('app', 'governance_schema_columns_opened', { assets: assets.length, columns: groups.reduce((count, group) => count + group.columns.length, 0) });
            return next;
          });
        }}
      >
        <Columns3 size={14} />
      </button>
      {open ? (
        <div className={`absolute right-0 z-40 mt-1 w-80 overflow-hidden rounded-none border shadow-md ${panel}`}>
          <div className={`flex items-center justify-between px-2 py-1.5 text-[11px] ${isLight ? 'bg-[#F7F8FA] text-[#3C4048]' : 'bg-white/[0.04] text-slate-300'}`}>
            <span>Schema columns</span>
          </div>
          <div className="relative px-2 py-1.5">
            <Search size={12} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${muted}`} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter columns"
              className={`${fieldClass(isLight)} h-7 !rounded-none !py-0 pl-7 text-[13px]`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {loading && !visible.length ? (
              <p className={`px-2.5 py-1.5 text-[12px] ${muted}`}>Loading columns…</p>
            ) : error && !visible.length ? (
              <p className={`px-2.5 py-1.5 text-[12px] ${isLight ? 'text-rose-600' : 'text-rose-300'}`}>{error}</p>
            ) : !visible.length ? (
              <p className={`px-2.5 py-1.5 text-[12px] ${muted}`}>{needle ? 'No matches' : 'Select an asset in Applies to to list columns.'}</p>
            ) : visible.map((group) => (
              <div key={group.id} className="mb-1">
                <p className={`truncate px-2.5 py-1 text-[11px] font-medium ${muted}`} title={group.label}>{group.label}</p>
                {group.columns.map((column) => (
                  <div key={`${column.assetId}:${column.name}`} className={`flex items-center gap-1.5 px-2.5 py-1 ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}>
                    <div className="min-w-0 flex-1">
                      <p className={`truncate font-mono text-[11px] ${isLight ? 'text-[#1C1C1C]' : 'text-slate-100'}`} title={column.qualified}>{column.qualified}</p>
                      <p className={`truncate text-[10px] ${muted}`}>{column.label}</p>
                    </div>
                    <button
                      type="button"
                      className={`${iconBtn(isLight)} !h-6 !w-6 !rounded-none`}
                      aria-label={`Copy ${column.qualified}`}
                      title={`Copy ${column.qualified}`}
                      onClick={() => {
                        onCopy(column.qualified);
                        logDataModels('app', 'governance_schema_column_copied', { name: column.name, path: column.qualified });
                      }}
                    >
                      <Copy size={11} />
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function uniqueColumns(assets: PolicyAsset[]) {
  const map = new Map<string, { name: string; type: string; classification?: string; assets: string[] }>();
  assets.forEach((asset) => {
    asset.columns.forEach((column) => {
      const current = map.get(column.name) ?? { name: column.name, type: column.type, classification: column.classification, assets: [] };
      current.assets.push(asset.name);
      map.set(column.name, current);
    });
  });
  return Array.from(map.values());
}

export default function ExpressionEditor({
  value,
  onChange,
  assets,
  isLight,
  policyType = 'ROW_LEVEL',
  variant = 'plain',
  readOnly,
  testUserId,
  onTestUserId,
  testResult,
  onTestResult,
  onRunTest,
  onCopyNotice,
  onOpenHelp,
}: {
  value: string;
  onChange: (value: string) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  policyType?: PolicyType;
  variant?: 'plain' | 'workbench';
  readOnly?: boolean;
  testUserId?: string;
  onTestUserId?: (id: string) => void;
  testResult?: QueryTestResult | null;
  onTestResult?: (result: QueryTestResult) => void;
  onRunTest?: () => void;
  onCopyNotice?: (message: string) => void;
  onOpenHelp?: () => void;
}) {
  const directory = usePolicyDirectory();
  const [helpOpen, setHelpOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [localUser, setLocalUser] = useState('');
  const [localResult, setLocalResult] = useState<QueryTestResult | null>(null);
  const userId = testUserId ?? localUser;
  const setUserId = onTestUserId ?? setLocalUser;

  useEffect(() => {
    const next = resolveDirectoryUserId(userId, directory.simUsers);
    if (next !== userId) setUserId(next);
  }, [directory.simUsers, setUserId, userId]);
  const shownResult = testResult !== undefined ? testResult : localResult;
  const extensions = useMemo(() => {
    const theme = EditorView.theme({
      '&': { fontSize: '12px' },
      '.cm-content': { minHeight: variant === 'workbench' ? '180px' : '88px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
      '.cm-tooltip': { borderRadius: '8px' },
    });
    const lint = linter((view) => {
      const result = validateExpression(view.state.doc.toString(), assets);
      return result.issues.map((issue): Diagnostic => ({
        from: Math.min(issue.from, view.state.doc.length),
        to: Math.min(Math.max(issue.to, issue.from + 1), view.state.doc.length),
        severity: issue.severity,
        message: issue.message,
      }));
    });
    const hover = hoverTooltip((view, pos) => {
      const word = view.state.wordAt(pos);
      if (!word) return null;
      const text = view.state.sliceDoc(word.from, word.to);
      const variable = ALL_VARIABLES.find((item) => item.path.endsWith(text) || item.path === text);
      const fn = FUNCTION_REGISTRY.find((item) => item.name === text.toUpperCase());
      const column = uniqueColumns(assets).find((item) => item.name === text || item.assets.some((assetName) => text.endsWith(`.${item.name}`)));
      const info = variable
        ? `${variable.path} — ${variable.type}\n${variable.description}\nExample: ${variable.example}`
        : fn
          ? `${fn.signature}\n${fn.description}`
          : column
            ? `${column.name} — ${column.type}${column.classification ? ` · ${column.classification}` : ''}`
            : null;
      if (!info) return null;
      return {
        pos: word.from,
        end: word.to,
        create() {
          const dom = document.createElement('div');
          dom.style.whiteSpace = 'pre-wrap';
          dom.style.maxWidth = '280px';
          dom.style.padding = '6px 8px';
          dom.style.fontSize = '12px';
          dom.textContent = info;
          return { dom };
        },
      };
    });
    return [DSL, autocompletion({ override: [completions(assets)] }), lint, hover, theme, EditorView.lineWrapping];
  }, [assets, variant]);

  const runTest = () => {
    if (onRunTest) {
      onRunTest();
      return;
    }
    const user = userById(userId, directory.simUsers);
    if (!user) {
      logDataModels('warning', 'governance_query_tested', { ok: false, reason: 'no_user', userId, policyType });
      return;
    }
    const result = testExpressionQuery(value, assets, user);
    setLocalResult(result);
    onTestResult?.(result);
    logDataModels(result.ok ? 'app' : 'warning', 'governance_query_tested', {
      ok: result.ok,
      passed: result.passed ?? false,
      userId,
      policyType,
    });
  };

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text);
    onCopyNotice?.('Copied to clipboard');
  };

  const openHelp = () => {
    if (onOpenHelp) {
      onOpenHelp();
      return;
    }
    logDataModels('app', 'governance_expression_help_opened', { policyType, source: 'workbench' });
    setHelpOpen(true);
  };

  return (
    <div className="space-y-2">
      {variant === 'workbench' ? (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <label className={`flex items-center gap-1.5 ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
            <span className="text-[11px]">As</span>
            <select
              className={`h-7 w-[7.25rem] rounded-md border px-1.5 text-[12px] leading-none focus:outline-none ${
                isLight
                  ? 'border-[#D8DCE2] bg-white text-[#1C1C1C] focus:border-[#1B6CA8]'
                  : 'border-white/15 bg-black text-slate-100 focus:border-white/30'
              }`}
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              aria-label="Test as user"
              disabled={!directory.simUsers.length}
            >
              {!directory.simUsers.length ? <option value="">{directory.loading ? 'Loading…' : 'No people'}</option> : null}
              {directory.simUsers.map((user) => (
                <option key={user.id} value={user.id}>{user.name}{user.department ? ` · ${user.department}` : user.role ? ` · ${user.role}` : ''}</option>
              ))}
            </select>
          </label>
          <button type="button" className={ghostBtn(isLight)} onClick={runTest}>
            <FlaskConical size={14} />
            Test query
          </button>
          <button
            type="button"
            className={`${iconBtn(isLight)} !h-8 !w-8`}
            aria-label="Expression copilot"
            title="Expression copilot"
            onClick={() => setCopilotOpen(true)}
          >
            <Sparkles size={14} />
          </button>
          <button type="button" className={ghostBtn(isLight)} onClick={openHelp}>
            <CircleHelp size={14} />
            Help
          </button>
          <button type="button" className={ghostBtn(isLight)} onClick={() => setBuilderOpen(true)}>
            <Table2 size={14} />
            Query builder
          </button>
          <SchemaColumnsButton assets={assets} isLight={isLight} onCopy={copy} />
        </div>
      ) : (
        <div className="flex items-center justify-end">
          <SchemaColumnsButton assets={assets} isLight={isLight} onCopy={copy} />
        </div>
      )}
      <div className={`overflow-hidden rounded-md border ${isLight ? 'border-[#D8DCE2]' : 'border-white/15'}`}>
        <CodeMirror
          value={value}
          height={variant === 'workbench' ? '200px' : '120px'}
          theme={isLight ? 'light' : 'dark'}
          extensions={extensions}
          editable={!readOnly}
          basicSetup={{ lineNumbers: false, foldGutter: false, highlightActiveLine: false }}
          onChange={onChange}
        />
      </div>
      {variant === 'workbench' && shownResult ? (
        <div
          className={`rounded-md border px-3 py-2 text-[12px] ${
            !shownResult.ok
              ? isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/30 bg-red-500/10 text-red-200'
              : shownResult.passed
                ? isLight ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                : isLight ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
          }`}
        >
          <p className="font-medium">{shownResult.ok ? (shownResult.passed ? 'True' : 'False') : 'Failed'}</p>
          <p className="mt-0.5">{shownResult.explanation}</p>
          {shownResult.errors.length ? shownResult.errors.map((error) => <p key={error}>{error}</p>) : null}
        </div>
      ) : null}
      {helpOpen ? (
        <ExpressionHelpDialog
          isLight={isLight}
          policyType={policyType}
          onClose={() => setHelpOpen(false)}
          onCopy={copy}
          canInsert={!readOnly}
          onInsert={(expression) => {
            if (readOnly) return;
            onChange(expression);
            setHelpOpen(false);
            logDataModels('app', 'governance_expression_example_inserted', { policyType, length: expression.length });
          }}
        />
      ) : null}
      {copilotOpen ? (
        <ExpressionCopilotDialog
          isLight={isLight}
          policyType={policyType}
          assets={assets}
          currentExpression={value}
          readOnly={readOnly}
          onClose={() => setCopilotOpen(false)}
          onCopy={copy}
          onUse={(expression) => {
            if (readOnly) return;
            onChange(expression);
            setCopilotOpen(false);
          }}
        />
      ) : null}
      {builderOpen ? (
        <QueryBuilderDialog
          isLight={isLight}
          assets={assets}
          expression={value}
          userId={userId}
          onUserId={setUserId}
          readOnly={readOnly}
          onClose={() => setBuilderOpen(false)}
          onApplyFilter={(where) => {
            if (readOnly) return;
            onChange(where);
          }}
        />
      ) : null}
    </div>
  );
}
